import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TimezoneService } from '@lx/quote-tool/shared/services';
import { Timezone } from '@lx/quote-tool/shared/types';
import { getBrowserTimeZone } from '@lx/quote-tool/shared/utils/functions';
import { CodeAndName } from '@lx/shared/types';
import { ListboxModule } from 'primeng/listbox';
import { delay, map, Observable, of, take } from 'rxjs';
import { SelectableField } from '@lx/quote-tool/lite/shared/types';
import { SearcherComponent, ListBoxComponent } from '@lx/quote-tool/lite/shared/ui/components';

@Component({
  standalone: true,
  selector: 'lx-timezone-selector',
  templateUrl: './timezone-selector.component.html',
  styleUrls: ['./timezone-selector.component.scss'],
  imports: [ListboxModule, FormsModule, AsyncPipe, SearcherComponent, ListBoxComponent],
})
export class TimezoneSelectorComponent implements OnInit {
  /**
   * The currently selected timezone from the parent component
   */
  @Input() selectedTimezoneValue: Timezone | undefined;

  /**
   * Flag that indicates if the component is visible or not
   */
  @Input() visible = false;

  @Output() selectedTimezoneChanged = new EventEmitter<SelectableField<Timezone>>();

  selectedTimezone: CodeAndName | undefined;

  timezones$: Observable<CodeAndName[]> | undefined;

  private readonly timezoneService = inject(TimezoneService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.initTimezoneData();
  }

  triggerSelectedTimezoneChanged(): void {
    this.selectedTimezoneChanged.emit({
      value: this.buildTimezone(this.selectedTimezone),
      fromUser: true,
    });
  }

  filterTimezone(query: string): void {
    this.timezones$ = this.timezoneService.timezones$.pipe(
      take(1),
      map((timezones) => {
        const filteredTimezones = timezones.filter((timezone) => timezone.name.toLowerCase().includes(query.toLowerCase()));
        return this.moveSelectedTimezoneToTop(filteredTimezones, this.selectedTimezone?.code);
      }),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  /**
   * It initializes the timezone data by using the selected timezone input and the timezones observable,
   * then prepares the timezones for display. Does NOT emit events - only sets internal state.
   */
  private initTimezoneData(): void {
    this.timezoneService.timezones$
      .pipe(
        take(1),
        map((timezones) => this.prepareTimezonesForDisplay(timezones, this.selectedTimezoneValue)),
        delay(500),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ timezonesWithSelectedFirst, selectedTimezone }) => {
        this.selectedTimezone = selectedTimezone;
        this.timezones$ = of(timezonesWithSelectedFirst);
      });
  }

  private prepareTimezonesForDisplay(
    timezones: CodeAndName[],
    selectedTimeZone?: Timezone,
  ): {
    timezonesWithSelectedFirst: CodeAndName[];
    selectedTimezone: CodeAndName;
  } {
    const selectedTimezoneCode = selectedTimeZone?.timezone ?? getBrowserTimeZone();
    const timezonesWithSelectedFirst = this.moveSelectedTimezoneToTop(timezones, selectedTimezoneCode);
    const selectedTimezone = timezonesWithSelectedFirst[0];

    return { timezonesWithSelectedFirst, selectedTimezone };
  }

  private moveSelectedTimezoneToTop(allTimezones: CodeAndName[], selectedTimezoneCode: string): CodeAndName[] {
    const selectedTimezone = allTimezones.find((timezone) => timezone.code === selectedTimezoneCode);
    const otherTimezones = allTimezones.filter((timezone) => timezone.code !== selectedTimezoneCode);
    return selectedTimezone ? [selectedTimezone, ...otherTimezones] : otherTimezones;
  }

  private buildTimezone(timezone: CodeAndName): Timezone {
    return this.timezoneService.buildTimezoneFromCode(timezone);
  }
}
